import Vue from "vue";
import * as types from "./mutation-types";

const actions = {};

//---------------------//
//  HELPER FUNCTIONS
//---------------------//
function isAuthMember(memberId) {
  let member = Vue.auth.user();
  // note, auth.user() can be empty if logged out
  // so, ensure "id" exists here
  return Vue._.get(member, "id", false) === memberId;
}

//---------------------//
//   HELPER ACTIONS
//---------------------//
const helperActions = {
  setLoadedStatus({ commit }, data) {
    commit(types.SET_LOADED_STATUS, { key: data.key, value: data.value });
  }
};

//---------------------//
//  BADGE ACTIONS
//---------------------//
const badgeActions = {
  fetchMemberBadges({ commit }, memberId) {
    return Vue.axios.get(`members/${memberId}/badges`).then(response => {
      commit(types.SET_MEMBER_BADGES, response.data);

      return response;
    });
  },

  fetchBadges({ commit }, category) {
    commit(types.SET_LOADED_BADGES, { key: "badgesLoaded", value: false });

    return Vue.axios.get(`badges/${category}`).then(response => {
      return response.data.data;
    });
  }
};

//---------------------//
//  CHALLENGE ACTIONS
//---------------------//
const challengeActions = {
  fetchPointsInChallenge(_, payload) {
    let { memberId, challengeId } = payload;

    return Vue.axios.get(`members/${memberId}/points-in-challenge`, {
      params: {
        challengeId
      }
    });
  },

  fetchChallenges({ commit }, memberId) {
    commit(types.SET_LOADED_STATUS, { key: "challengesLoaded", value: false });

    return Vue.axios
      .get(`members/${memberId}/challenges`)
      .then(response => {
        if (isAuthMember(memberId)) {
          commit(types.SET_CHALLENGES, response.data.data);
        }

        commit(types.SET_CHALLENGES_BY_MEMBER_ID, {
          key: `challenges-${memberId}`,
          value: response.data.data
        });

        commit(types.SET_LOADED_STATUS, {
          key: "challengesLoaded",
          value: true
        });
      })
      .catch(() => {
        commit(types.SET_LOADED_STATUS, {
          key: "challengesLoaded",
          value: true
        });
      });
  },

  fetchArchivedChallenges({ commit }, payload) {
    const { memberId, page } = payload;

    commit(types.SET_LOADED_STATUS, {
      key: "archivedChallengesLoaded",
      value: false
    });

    const thisPage = page ? `&page=${page}` : "";

    return Vue.axios
      .get(`members/${memberId}/challenges?status=archived${thisPage}`)
      .then(response => {
        commit(types.SET_ARCHIVED_CHALLENGES, response.data);
        commit(types.SET_LOADED_STATUS, {
          key: "archivedChallengesLoaded",
          value: true
        });
      })
      .catch(() => {
        commit(types.SET_LOADED_STATUS, {
          key: "archivedChallengesLoaded",
          value: true
        });
      });
  },

  addCompletionDateForPreventativeChallenge(_, payload) {
    const { memberId, challengeId, completionDate } = payload;
    let params = {
      challengeId,
      completionDate
    };
    return Vue.axios
      .post(
        `members/${memberId}/preventative-challenge/add-completion-date`,
        params
      )
      .then(response => {
        return response.data;
      })
      .catch(error => {
        throw error.response;
      });
  },

  addFreeTextForPreventativeChallenge(_, payload) {
    const { memberId, challengeId, freeText } = payload;
    let params = {
      challengeId,
      freeText
    };
    return Vue.axios
      .post(`members/${memberId}/preventative-challenge/add-free-text`, params)
      .then(response => {
        return response.data.data;
      })
      .catch(error => {
        throw error.response;
      });
  }
};

//------------------//
//  MEMBER ACTIONS
//------------------//
const memberActions = {
  fetchMember({ commit }, data) {
    // endpoint takes id or username
    commit(types.SET_LOADED_STATUS, { key: "memberLoaded", value: false });
    return Vue.axios
      .get(`/members/${data.memberId}`)
      .then(response => {
        if (data.hasOwnProperty("setMember") && data.setMember) {
          const memberData = response.data.data;
          if (memberData.questions) {
            memberData.answeredQuestions = memberData.questions.filter(
              q => q.memberAnswered === true
            );
            memberData.unansweredQuestions = memberData.questions.filter(
              q => !q.memberAnswered
            );
          }
          commit(types.SET_MEMBER, response.data.data);
        }

        if (data.hasOwnProperty("setMemberByName") && data.setMemberByName) {
          commit(types.SET_MEMBER_BY_NAME, {
            key: `member-${data.memberId}`,
            value: response.data.data
          });
        }

        commit(types.SET_LOADED_STATUS, { key: "memberLoaded", value: true });
        return response.data.data;
      })
      .catch(error => {
        commit(types.SET_LOADED_STATUS, { key: "memberLoaded", value: true });
        return Vue._.get(error, "response.data", "Error.");
      });
  },

  fetchActivities({ commit }, programId) {
    let url = `activities?programId=${programId}&forMembers=1`;

    commit(types.SET_LOADING_STATUS, { key: "activitiesLoading", value: true });

    return Vue.axios.get(url).then(response => {
      const activities = response.data.data;

      commit(types.SET_LOADING_STATUS, {
        key: "activitiesLoading",
        value: false
      });
      commit(types.SET_LOADED_STATUS, { key: "activitiesLoaded", value: true });
      commit(types.SET_ACTIVITIES, activities);
    });
  },

  updateMember({ commit, dispatch }, payload) {
    const { memberId, formData } = payload;

    return Vue.axios
      .post(`members/${memberId}`, formData)
      .then(response => {
        // this updateMember action is used in the admin area for admin to update any member
        // the SET_MEMBER mutation is only for the logged in member
        if (isAuthMember(memberId)) {
          const memberData = response.data.data;
          if (memberData.questions) {
            memberData.answeredQuestions = memberData.questions.filter(
              q => q.memberAnswered === true
            );
            memberData.unansweredQuestions = memberData.questions.filter(
              q => !q.memberAnswered
            );
          }
          commit(types.SET_MEMBER, response.data.data);
          // the member object is also used via auth.user()
          // this is how to refresh that data too
          Vue.auth.fetch();
        }

        return response;
      })
      .catch(error => {
        if (error.error) {
          if (
            error.error.code === "validation_failed" &&
            this._.has(error, "error.fields.image")
          ) {
            dispatch(
              "snackBar",
              {
                message: Vue._.get(
                  error,
                  "error.fields.image[0]",
                  `An error has occurred.
                Please try again. Or please reach out to wellness@be-engaged.co.`
                ),
                type: "is-danger",
                queue: false,
                duration: 10000
              },
              { root: true }
            );
          } else {
            dispatch(
              "snackBar",
              {
                message: `An error has occurred.
                Please try again. Or please reach out to wellness@be-engaged.co.`,
                type: "is-danger",
                queue: false,
                duration: 10000
              },
              { root: true }
            );
          }
        }
        // TODO refactor to throw, so we can use try/catch where updateMember is used
        return Vue._.get(error, "response.data", "Error.");
      });
  },

  updateMemberCopy({ commit }, data) {
    commit(types.UPDATE_MEMBER_COPY_VALUE, data);
  },

  updateMemberValue({ commit }, data) {
    commit(types.UPDATE_MEMBER_VALUE, data);
  },

  async changePassword(_, payload) {
    const { memberId, formData } = payload;

    let response = null;

    try {
      response = await Vue.axios.post(
        `members/${memberId}/reset-password`,
        formData
      );
    } catch (error) {
      response = error.response;
    }

    return response;
  },

  async delete({ commit }, memberId) {
    let response = null;
    commit(types.SET_LOADED_STATUS, { key: "deleteInProgress", value: true });

    try {
      response = await Vue.axios.delete(`members/${memberId}`);
    } catch (error) {
      response = error.response;
    }

    commit(types.SET_LOADED_STATUS, { key: "deleteInProgress", value: false });
    return response;
  },

  accountDownload(_, memberId) {
    let response = null;

    try {
      return Vue.axios.get(`members/${memberId}/account-download`);
    } catch (error) {
      response = error.response;
    }

    return response;
  },

  async fetchMilestones({ commit }, payload) {
    const { memberId, challengeId } = payload;

    commit(types.SET_LOADED_STATUS, {
      key: "milestonesLoaded",
      value: false
    });

    const response = await Vue.axios.get(
      `members/${memberId}/milestones?challengeId=${challengeId}`
    );

    commit(types.SET_MILESTONES, {
      key: "milestones" + challengeId,
      value: response.data.data
    });
    commit(types.SET_LOADED_STATUS, {
      key: "milestonesLoaded",
      value: true
    });
  },

  async fetchPoints({ commit }, payload) {
    let { memberId, ...filters } = payload;

    commit(types.SET_LOADED_STATUS, {
      key: "pointsLoaded",
      value: false
    });
    const response = await Vue.axios.get(`members/${memberId}/points`, {
      params: filters
    });
    commit(types.SET_LOADED_STATUS, {
      key: "pointsLoaded",
      value: true
    });
    return response.data;
  },

  fetchMemberStepData({ commit }, payload) {
    let { startDate, endDate, memberId, perPage = 7, setById } = payload;

    startDate = startDate ? `&startDate=${startDate}` : "";
    endDate = endDate ? `&endDate=${endDate}` : "";
    return Vue.axios
      .get(
        `members/${memberId}/step-data?perPage=${perPage}${startDate}${endDate}`
      )
      .then(response => {
        if (setById) {
          commit(types.SET_STEP_DATA_BY_MEMBER_ID, {
            key: `stepData-${memberId}`,
            value: response.data.data
          });
        } else {
          commit(types.SET_STEP_DATA, response.data.data);
        }
        commit(types.SET_LOADED_STATUS, { key: "stepDataLoaded", value: true });
        return response;
      })
      .catch(error => {
        throw Vue._.get(error, "response.data", "Error.");
      });
  },

  fetchMemberWellnessDataByFilter({ commit }, payload) {
    let {
      startDate,
      endDate,
      memberId,
      perPage = 7,
      metric,
      setById
    } = payload;

    startDate = startDate ? `&startDate=${startDate}` : "";
    endDate = endDate ? `&endDate=${endDate}` : "";
    return Vue.axios
      .get(
        `members/${memberId}/wellness-metric-data?metric=${metric}&perPage=${perPage}${startDate}${endDate}`
      )
      .then(response => {
        if (setById) {
          commit(types.SET_METRIC_DATA_BY_MEMBER_ID, {
            key: `metricData-${memberId}`,
            value: response.data.data
          });
        } else {
          commit(types.SET_STEP_DATA, response.data.data);
        }
        commit(types.SET_LOADED_STATUS, {
          key: "metricDataLoaded",
          value: true
        });
        return response;
      })
      .catch(error => {
        throw Vue._.get(error, "response.data", "Error.");
      });
  },
  fetchMemberWellnessData({ commit }, payload) {
    let { memberId } = payload;

    return Vue.axios
      .get(`members/${memberId}/wellness-metric-data`, { params: payload })
      .then(response => {
        commit(types.SET_WELLNESS_CHART_DATA, response.data.data);
        commit(types.SET_LOADED_STATUS, {
          key: "wellnessChartDataLoaded",
          value: true
        });
        return response;
      })
      .catch(error => {
        throw Vue._.get(error, "response.data", "Error.");
      });
  },

  updateMemberStepData(_, payload) {
    const { stepData, memberId } = payload;

    if (!memberId) throw "No Member ID.";

    return Vue.axios
      .post(`members/${memberId}/add-steps`, {
        stepData: stepData
      })
      .then(response => response)
      .catch(error => {
        throw error.response.data;
      });
  },
  updateMemberWellnessData(_, payload) {
    const { wellnessMetricsData, metricType, memberId } = payload;

    if (!memberId) throw "No Member ID.";

    return Vue.axios
      .post(`members/${memberId}/add-wellness-metrics`, {
        wellnessMetricsData: wellnessMetricsData,
        metricType: metricType
      })
      .then(response => response)
      .catch(error => {
        throw error.response.data;
      });
  },
  fetchLeaderboard({ commit }, payload) {
    const { memberId } = payload;

    commit(types.SET_LOADED_STATUS, { key: "leaderboardLoaded", value: false });

    return Vue.axios.get(`members/${memberId}/leaderboard`).then(response => {
      commit(types.SET_LEADERBOARD_BY_MEMBER_ID, {
        key: `leaderboard-${memberId}`,
        value: response.data
      });
      commit(types.SET_LOADED_STATUS, {
        key: "leaderboardLoaded",
        value: true
      });
    });
  }
};

//----------------//
//  TEAM ACTIONS
//----------------//
const teamActions = {
  fetchMemberTeams({ commit }, memberId) {
    return Vue.axios.get(`/members/${memberId}/teams`).then(response => {
      if (isAuthMember(memberId)) {
        commit(types.SET_MEMBER_TEAMS, response.data.data);
      }

      commit(types.SET_TEAMS_BY_MEMBER_ID, {
        key: `teams-${memberId}`,
        value: response.data.data
      });

      commit(types.SET_LOADED_STATUS, {
        key: "memberTeamsLoaded",
        value: true
      });

      return response.data.data;
    });
  },

  fetchTeamsMemberIsAdmin({ commit }, memberId) {
    return Vue.axios.get(`/members/${memberId}/teams-admin`).then(response => {
      commit(types.SET_MEMBER_TEAMS_ADMIN, response.data.data);
      commit(types.SET_LOADED_STATUS, {
        key: "teamsMemberIsAdminLoaded",
        value: true
      });
      return response;
    });
  },

  updateTeamInTeamsMemberIsAdmin({ commit }, team) {
    commit(types.UPDATE_TEAMS_MEMBER_IS_ADMIN, team);
  },

  updateTeamInMemberTeams({ commit }, team) {
    commit(types.UPDATE_MEMBER_TEAM, team);
  }
};

//------------------//
//  INVITE ACTIONS
//------------------//
const inviteActions = {
  fetchChallengeInvites({ commit }, payload) {
    const { memberId, recipient } = payload;

    return Vue.axios
      .get(
        `members/${memberId}/invites?type=competitions&recipient=${recipient}`
      )
      .then(response => {
        commit(types.SET_CHALLENGE_INVITES, response.data.data);

        return response.data.data;
      });
  },

  fetchMemberTeamInvites({ commit }, payload) {
    const { memberId } = payload;

    return Vue.axios
      .get(`members/${memberId}/invites`, {
        params: { type: "groups", recipient: 1 }
      })
      .then(response => {
        commit(types.SET_MEMBER_TEAM_INVITES, response.data.data);
        commit(types.SET_LOADED_STATUS, {
          key: "memberTeamInvitesLoaded",
          value: true
        });

        return response.data.data;
      });
  },

  fetchFriendsRequesting({ commit }, memberId) {
    // these are invites where the member has been invited.

    commit(types.SET_LOADED_STATUS, {
      key: "friendsRequestingLoaded",
      value: false
    });

    return Vue.axios
      .get(`members/${memberId}/invites?type=friends&recipient=1`)
      .then(response => {
        commit(types.SET_FRIENDS_REQUESTING, response.data.data);
        commit(types.SET_LOADED_STATUS, {
          key: "friendsRequestingLoaded",
          value: true
        });

        return response.data.data;
      });
  },

  fetchFriendsInvited({ commit }, memberId) {
    // these are invites the member has generated.

    commit(types.SET_LOADED_STATUS, {
      key: "friendsInvitedLoaded",
      value: false
    });

    return Vue.axios
      .get(`members/${memberId}/invites?type=friends&recipient=0`)
      .then(response => {
        commit(types.SET_FRIENDS_INVITED, response.data.data);
        commit(types.SET_LOADED_STATUS, {
          key: "friendsInvitedLoaded",
          value: true
        });

        return response.data.data;
      });
  },

  acceptFriendInvite({ dispatch }, payload) {
    const {
      memberId,
      friendId,
      friendUsername,
      invitePrimaryId,
      invite
    } = payload;

    const data = {};
    if (invitePrimaryId) {
      Object.assign(data, { invitePrimaryId });
    }
    if (invite) {
      Object.assign(data, { invite });
    }
    if (friendId) {
      Object.assign(data, { friendId });
    }

    return Vue.axios
      .post(`members/${memberId}/add-friend`, data)
      .then(response => {
        dispatch(
          "snackBar",
          {
            message: `You are now friends with ${friendUsername}!`,
            type: "is-success",
            queue: false,
            duration: 10000
          },
          { root: true }
        );
        return response;
      })
      .catch(error => {
        dispatch(
          "snackBar",
          {
            message: error.response.data.error.message,
            type: "is-danger",
            queue: false,
            duration: 10000
          },
          { root: true }
        );
        return error.response.data;
      });
  },

  sendFriendInvite({ dispatch }, payload) {
    const { recipientId } = payload;

    const data = {
      type: "friends",
      recipientIds: [recipientId]
    };

    return Vue.axios.post("invites", data).catch(error => {
      dispatch(
        "snackBar",
        {
          message: error.response.data.error.message,
          type: "is-danger",
          queue: false,
          duration: 10000
        },
        { root: true }
      );
      throw error.response;
    });
  },

  setSendFriendInviteProcessingForMember({ commit }, value) {
    commit(types.SET_LOADED_STATUS, {
      key: "sendFriendInviteProcessing",
      value: value
    });
  },

  setAcceptFriendInviteProcessingForMember({ commit }, value) {
    commit(types.SET_LOADED_STATUS, {
      key: "acceptFriendInviteProcessing",
      value: value
    });
  }
};

//-------------------//
//  MESSAGE ACTIONS
//-------------------//
const messageActions = {
  fetchRequestedJoinMessages({ commit }, memberId) {
    return Vue.axios
      .get(`members/${memberId}/messages?type=joinrequest&isOriginator=1`)
      .then(response => {
        commit(types.SET_JOIN_REQUEST_MESSAGES, response.data.data);

        return response.data.data;
      });
  },

  fetchMessages({ commit }, payload) {
    const { memberId, page, perPage } = payload;

    const thisPage = page ? `&page=${page}` : "";
    const perThisPage = perPage ? `&perPage=${perPage}` : "&perPage=10";

    return Vue.axios
      .get(`members/${memberId}/messages?${thisPage}${perThisPage}`)
      .then(response => {
        commit(types.SET_MESSAGES, response.data);
        commit(types.SET_MESSAGE_COUNT, response.data.unreadCount);
      });
  },

  markMessageRead({ commit, state }, messageId) {
    commit(types.SET_MESSAGE_COUNT, state.messageCount - 1);
    Vue.axios.post(`messages/${messageId}/mark-read`);
  },

  markAllMessagesRead({ commit }, params) {
    const { memberId } = params;
    commit(types.SET_MESSAGE_COUNT, 0);
    Vue.axios.post(`messages/mark-all-read`, { memberId });
  }
};

//-------------------//
//  NOTICE ACTIONS
//-------------------//
const noticeActions = {
  fetchMemberNotice({ commit }, payload) {
    const { memberId } = payload;

    return Vue.axios.get(`members/${memberId}/notices`).then(response => {
      commit(types.SET_MEMBER_NOTICE, response.data.data);
      commit(types.SET_LOADED_STATUS, {
        key: "memberNoticeLoaded",
        value: true
      });
    });
  },

  fetchMemberOrganizationNotice({ commit }, payload) {
    const { memberId } = payload;

    return Vue.axios
      .get(`members/${memberId}/organization/notices`)
      .then(response => {
        commit(types.SET_MEMBER_ORGANIZATION_NOTICE, response.data.data);
        commit(types.SET_LOADED_STATUS, {
          key: "memberOrganizationNoticeLoaded",
          value: true
        });
      });
  }
};

//------------------//
//  FRIEND ACTIONS
//------------------//
const friendActions = {
  fetchFriends({ commit }, payload) {
    const { memberId } = payload;

    commit(types.SET_LOADED_STATUS, { key: "friendsLoaded", value: false });

    return Vue.axios.get(`members/${memberId}/friends`).then(response => {
      if (isAuthMember(memberId)) {
        commit(types.SET_MEMBER_FRIENDS, response.data.data);
      }

      commit(types.SET_MEMBER_FRIENDS_BY_MEMBER_ID, {
        key: `friends-${memberId}`,
        value: response.data.data
      });

      if (isAuthMember(memberId)) {
        commit(types.SET_LOADED_STATUS, { key: "friendsLoaded", value: true });
      }

      commit(types.SET_MEMBER_FRIENDS_LOADED_STATUS, {
        key: `friendsLoaded-${memberId}`,
        value: true
      });
    });
  },

  removeFriend({ dispatch }, payload) {
    const { memberId, friendId, friendUsername } = payload;

    if (!(memberId && friendId))
      return {
        error: true,
        message: "You need both the member's id and friend's id"
      };

    const data = {
      friendId: friendId
    };

    return Vue.axios
      .post(`members/${memberId}/remove-friend`, data)
      .then(response => {
        dispatch(
          "snackBar",
          {
            message: `You are no longer friends with ${friendUsername}!`,
            type: "is-success",
            queue: false,
            duration: 10000
          },
          { root: true }
        );
        return response;
      })
      .catch(error => {
        dispatch(
          "snackBar",
          {
            message: error.response.data.error.message,
            type: "is-danger",
            queue: false,
            duration: 10000
          },
          { root: true }
        );
        throw error.response.data;
      });
  },

  setRemoveFriendProcessingForMember({ commit }, value) {
    commit(types.SET_LOADED_STATUS, {
      key: "removeFriendProcessing",
      value: value
    });
  }
};

//--------------------//
//  JOURNAL ACTIONS
//--------------------//
const journalActions = {
  async fetchJournals({ commit }, payload) {
    let { memberId, isProfile, photosOnly, filter, page, resetList } = payload;

    let params = {
      filter: filter ? `friends` : `self`,
      photosOnly: photosOnly ? 1 : "",
      page: page ? page : "",
      includeMetaCounts: 1
    };

    commit(types.SET_LOADED_STATUS, { key: "journalsLoaded", value: false });

    // if we change our filters, then we need to blow away our list of journal entries.
    if (resetList === true || !page || page == 1) {
      if (isProfile) {
        commit(types.RESET_JOURNALS_BY_MEMBER_ID, `journals-${memberId}`);
      } else {
        commit(types.RESET_JOURNALS);
      }
    }

    return Vue.axios
      .get(`members/${memberId}/journals`, { params })
      .then(response => {
        let journalData = response.data.data;

        if (isAuthMember(memberId)) {
          let journalEntryStats = {
            totalCount: response.data.totalCount,
            publicCount: response.data.publicCount,
            friendsOnlyCount: response.data.friendsOnlyCount,
            privateCount: response.data.privateCount
          };

          commit(types.SET_JOURNAL_ENTRY_STATS, journalEntryStats);
        } else {
          commit(types.SET_JOURNAL_ENTRY_STATS, {});
        }

        // see if we've received any more data.
        if (journalData.length === 0) {
          commit(types.IS_JOURNAL_LAST_PAGE, true);
        } else {
          if (isProfile) {
            commit(types.SET_JOURNALS_BY_MEMBER_ID, {
              key: `journals-${memberId}`,
              value: journalData
            });
          } else {
            commit(types.SET_JOURNALS, journalData);
          }
        }

        commit(types.SET_LOADED_STATUS, { key: "journalsLoaded", value: true });
      });
  },

  fetchMakeAnEntryJournalByDate({ commit }, payload) {
    let { memberId, date } = payload;
    let params = {
      filter: "self",
      startDate: date || "",
      endDate: date || "",
      includeComments: 0
    };

    commit(types.SET_LOADED_STATUS, {
      key: "makeAnEntryJournalLoading",
      value: true
    });

    return Vue.axios
      .get(`members/${memberId}/journals`, {
        params
      })
      .then(response => {
        if (!Vue._.isEmpty(response.data.data)) {
          commit(types.SET_MAKE_AN_ENTRY_JOURNAL, response.data.data[0]);
        } else {
          commit(types.SET_MAKE_AN_ENTRY_JOURNAL, {});
        }

        commit(types.SET_LOADED_STATUS, {
          key: "makeAnEntryJournalLoading",
          value: false
        });

        return response.data.data;
      });
  },

  fetchJournalPageJournal({ commit, dispatch, state }, payload) {
    const { memberId, journalDate } = payload;

    const params = {
      filter: "self",
      startDate: journalDate,
      endDate: journalDate
    };

    commit(types.SET_LOADED_STATUS, {
      key: "journalPageJournalLoading",
      value: true
    });
    commit(types.SET_JOURNAL_PAGE_JOURNAL, {});

    return Vue.axios
      .get(`members/${memberId}/journals`, { params })
      .then(response => {
        const journal = Vue._.get(response, "data.data", null);

        if (journal) {
          commit(types.SET_JOURNAL_PAGE_JOURNAL, journal);

          if (journal.id === Vue._.get(state, "makeAnEntryJournal.id", null)) {
            commit(types.SET_MAKE_AN_ENTRY_JOURNAL, journal);
          }
        }

        commit(types.SET_LOADED_STATUS, {
          key: "journalPageJournalLoading",
          value: false
        });
      })
      .catch(error => {
        dispatch(
          "snackBar",
          {
            message: Vue._.get(error, "response.data.error.message", "Error."),
            type: "is-danger",
            queue: false,
            duration: 10000
          },
          { root: true }
        );
        commit(types.SET_LOADED_STATUS, {
          key: "journalPageJournalLoading",
          value: false
        });
      });
  },

  async storeJournal({ commit }, payload) {
    const { formData } = payload;

    let response = null;

    commit(types.SET_LOADED_STATUS, {
      key: "makeAnEntryJournalLoading",
      value: true
    });

    try {
      response = await Vue.axios.post(`journals`, formData);
      if (!Vue._.isEmpty(response.data.data)) {
        commit(types.SET_MAKE_AN_ENTRY_JOURNAL, response.data.data);
      }
    } catch (error) {
      response = error.response.data;
    }

    commit(types.SET_LOADED_STATUS, {
      key: "makeAnEntryJournalLoading",
      value: false
    });

    return response;
  },

  async updateJournal({ commit, state }, payload) {
    const { itemId, formData } = payload;

    let response = null;

    commit(types.SET_LOADED_STATUS, {
      key: "makeAnEntryJournalLoading",
      value: true
    });

    try {
      response = await Vue.axios.post(`journals/${itemId}`, formData);
      if (
        !Vue._.isEmpty(response.data.data) &&
        state.makeAnEntryJournal.id === response.data.data.id
      ) {
        commit(types.SET_MAKE_AN_ENTRY_JOURNAL, response.data.data);
      }
    } catch (error) {
      response = error.response.data;
    }

    commit(types.SET_LOADED_STATUS, {
      key: "makeAnEntryJournalLoading",
      value: false
    });

    return response;
  },

  async deleteJournalPhoto({ commit, dispatch }, payload) {
    const { itemId } = payload;

    return await Vue.axios
      .post(`journals/${itemId}/destroy-image`)
      .then(response => {
        commit(types.SET_MAKE_AN_ENTRY_JOURNAL, response.data.data);

        return response;
      })
      .catch(error => {
        dispatch(
          "snackBar",
          {
            message: error.response.data.error.message,
            type: "is-danger",
            queue: false,
            duration: 10000
          },
          { root: true }
        );

        return error.response.data;
      });
  },

  storeComment({ dispatch }, payload) {
    const { itemId, text } = payload;

    return Vue.axios
      .post(`journal-comments`, {
        journalId: itemId,
        text
      })
      .catch(error => {
        const response = error.response.data.error;
        dispatch(
          "snackBar",
          {
            message: response.message || response.fields.text[0],
            type: "is-danger",
            queue: false,
            duration: 10000
          },
          { root: true }
        );
      });
  },

  editComment({ dispatch }, payload) {
    const { itemId, text } = payload;

    return Vue.axios
      .put(`journal-comments/${itemId}`, { text })
      .catch(error => {
        const response = error.response.data.error;
        dispatch(
          "snackBar",
          {
            message: response.message || response.fields.text[0],
            type: "is-danger",
            queue: false,
            duration: 10000
          },
          { root: true }
        );
      });
  },

  async deleteComment({ dispatch }, payload) {
    const { itemId } = payload;

    return Vue.axios.delete(`journal-comments/${itemId}`).catch(error => {
      dispatch(
        "snackBar",
        {
          message: error.response.data.error.message,
          type: "is-danger",
          queue: false,
          duration: 10000
        },
        { root: true }
      );
    });
  }
};

//------------------//
//  DEVICE ACTIONS
//------------------//
const deviceActions = {
  disconnectDevice() {
    return Vue.axios
      .delete(`devices/disconnect`)
      .then(response => response)
      .catch(error => {
        throw error.response;
      });
  },

  syncDevice() {
    return Vue.axios
      .post(`devices/sync`)
      .then(response => response)
      .catch(error => {
        throw error.response;
      });
  }
};

//------------------//
//  MOBILE ACTIONS
//------------------//
const mobileActions = {
  mobileFetchChallenges({ commit }, mobileToken) {
    return Vue.axios
      .get(`mobile/members/${mobileToken}/challenges`)
      .then(response => {
        commit(types.SET_MOBILE_CHALLENGES, response.data.data);

        commit(types.SET_LOADED_STATUS, {
          key: "mobileChallengeLoaded",
          value: true
        });
      })
      .catch(() => {
        commit(types.SET_LOADED_STATUS, {
          key: "mobileChallengeLoaded",
          value: true
        });
      });
  },
  mobileAddCompletionDateForPreventativeChallenge(_, payload) {
    const { mobileToken, challengeId, completionDate } = payload;
    let params = {
      challengeId,
      completionDate,
      mobileToken
    };
    return Vue.axios
      .post(`mobile/preventative-challenge/add-completion-date`, params)
      .then(response => {
        return response.data;
      })
      .catch(error => {
        throw error.response;
      });
  },
  mobileAddFreeTextForPreventativeChallenge(_, payload) {
    const { mobileToken, challengeId, freeText } = payload;
    let params = {
      challengeId,
      freeText,
      mobileToken
    };
    return Vue.axios
      .post(`mobile/preventative-challenge/add-free-text`, params)
      .then(response => {
        return response.data.data;
      })
      .catch(error => {
        throw error.response;
      });
  },
  mobileFetchMemberData({ commit }, mobileToken) {
    let params = {
      mobileToken: mobileToken
    };
    return Vue.axios
      .get(`mobile/member-data`, { params })
      .then(response => {
        // set member object
        commit("members/SET_MEMBER", response.data.data, {
          root: true
        });
        commit(
          "members/SET_LOADED_STATUS",
          { key: "memberLoaded", value: true },
          {
            root: true
          }
        );

        return response.data.data;
      })
      .catch(error => {
        throw error.response;
      });
  }
};

Object.assign(
  actions,
  badgeActions,
  challengeActions,
  teamActions,
  inviteActions,
  memberActions,
  messageActions,
  friendActions,
  journalActions,
  deviceActions,
  helperActions,
  noticeActions,
  mobileActions
);

export default actions;
